import React, { useState, useEffect } from "react";
import { Box, Pagination, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AddButton } from "../button/button";
import FeedbackTable from "./FeedbackTable";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { useDispatch, useSelector } from "react-redux";
import { clearFeedbackRow, deleteFeedback, getFeedbackList, optimisticToggle, resetDeleteStatus, resetToggleStatus, singleFeedback, toggleFeedback } from "../../redux/slices/feedback/feedbackSlice";
import { STATUS } from "../../utils/status";
import SnackbarNotification from "../snackbar/snackbarNotification";
import DeleteModal from "../modal/deleteModal";
import { FeedbackPagination } from "./FeedbackPagination";


interface FeedbackEntry {
  id: string;
  stages: string[];
  recipient: string;
  vendor?: string | null
  feedback: string
  longDescription?: string
  is_enabled: boolean
  recipientCount: number
  count?: number

}

export default function GlobalFeedback() {
  const { feedbacks, getFeedbackStatus, getFeedbackMessage } = useSelector((state: RootState) => state.feedback as any);
  const [rows, setRows] = useState<FeedbackEntry[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userIdToDelete, setDeleteUserId] = useState<string>("");
  const [currentPage, setCurrentPage]: any = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const totalPages = Math.ceil((feedbacks?.count || 0) / itemsPerPage);

  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const toggleFeedbackStatus = useSelector((state: RootState) => state.feedback.toggleFeedbackStatus as any);
  const toggleFeedbackMessage = useSelector((state: RootState) => state.feedback.toggleFeedbackMessage as any);
  const deleteFeedbackStatus = useSelector((state: RootState) => state.feedback.deleteFeedbackStatus as any);
  const deleteFeedbackMessage = useSelector((state: RootState) => state.feedback.deleteFeedbackMessage as any);
  
  const handleToggleEnabled = (row: any) => {
    const newStatus = !row.is_enabled;
    dispatch(optimisticToggle({ id: row.id, is_enabled: newStatus }));

    dispatch(toggleFeedback({ id: row.id, is_enabled: newStatus }));
  };


  const handleDelete = (id: string) => {
    setShowDeleteModal(true);
    setDeleteUserId(id);
  };

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    setRows(prev => prev.filter(r => r.id !== id));
    try {
      await dispatch(deleteFeedback(userIdToDelete));

    } catch (error) {
      handleSnackbar(`Failed to delete feedback : ${error}`, 'error');
    }
  };

  const handleEdit = (row: FeedbackEntry) => {
    dispatch(clearFeedbackRow());
    navigate(`/feedback/edit/${id}/${row.id}`);
  };

  useEffect(() => {
    if (id) dispatch(getFeedbackList({ sub_project_id: id, page: currentPage, pageSize: itemsPerPage }));
  }, [id, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    const handleToggleFeedbackStatus = () => {
      if (toggleFeedbackStatus === STATUS.SUCESS) {
        handleSnackbar(toggleFeedbackMessage, 'success')
        dispatch(resetToggleStatus());
      }
      if (toggleFeedbackStatus === STATUS.FAILED) {
        handleSnackbar(toggleFeedbackMessage, 'error');
        dispatch(resetToggleStatus());
      }
    }
    handleToggleFeedbackStatus();
  }, [toggleFeedbackStatus]);


  useEffect(() => {
    const handleDeleteFeedbackStatus = () => {
      if (deleteFeedbackStatus === STATUS.SUCESS) {
        handleSnackbar(deleteFeedbackMessage, 'success')
        dispatch(resetDeleteStatus());
      }
      if (deleteFeedbackStatus === STATUS.FAILED) {
        handleSnackbar(deleteFeedbackMessage, 'error');
        dispatch(resetDeleteStatus());
      }
    }
    handleDeleteFeedbackStatus();
  }, [deleteFeedbackStatus]);


  const enabledCount = feedbacks?.results?.filter((item: any) => item.is_enabled).length ?? 0;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Box flexGrow={1} />

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            component="span"
            sx={{
              backgroundColor: "#E0F7FA",
              color: "#040079ff",
              fontSize: "11px",
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontFamily: "Montserrat",
            }}
          >Enabled: {enabledCount}</Box>
          <AddButton
            onClick={() => {
              dispatch(clearFeedbackRow());
              navigate(`/addfeedback/${id}`)
            }}
          >
            <AddIcon /> Create Feedback</AddButton>
        </Stack>
      </Box>

      <FeedbackTable
        rows={rows}
        handleEdit={(row: any) => handleEdit(row as FeedbackEntry)}
        handleToggleEnabled={handleToggleEnabled}
        handleDelete={handleDelete}
        feedbacks={feedbacks}
        getFeedbackStatus={getFeedbackStatus}
        getFeedbackMessage={getFeedbackMessage}
      />

      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      <DeleteModal
        open={showDeleteModal}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />
      <FeedbackPagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={paginate}
        itemsPerPage={itemsPerPage}
        data={feedbacks}
        dataStatus={getFeedbackStatus}
      />



    </Box >
  );
}